import { useMemo, useState } from 'react';
import { PERMISSIONS, permissionAllowedAt, type ScopeLevel } from '@arcnet/contracts';
import { Icon } from '../../components/ui/Icon.js';

/**
 * Every permission a role can hold, grouped by area (docs/33, owner: "every permission").
 *
 * Permissions that do not belong at the role's level are left out entirely; the ones the person
 * editing does not hold are shown, switched off, with the reason, because the API refuses them
 * anyway (nobody grants what they do not hold) and a silent gap would look like a missing feature.
 */

const AREAS: Array<{ label: string; resources: string[] }> = [
  { label: 'Account', resources: ['tenant', 'site', 'reseller', 'branding'] },
  { label: 'People and access', resources: ['user', 'role', 'membership', 'apikey', 'sso', 'impersonate'] },
  { label: 'Extensions and phones', resources: ['extension', 'device', 'handsetmodel', 'provisioningtemplate', 'featurecode', 'phonebook'] },
  { label: 'Call handling', resources: ['ringgroup', 'queue', 'voicemail', 'flow', 'inboundroute', 'outboundroute', 'dialpermission'] },
  { label: 'Numbers', resources: ['number', 'port'] },
  { label: 'Calls and recordings', resources: ['cdr', 'recording', 'report'] },
  { label: 'Billing', resources: ['billing', 'ratedeck', 'invoice', 'payment', 'balance', 'creditlimit', 'product'] },
  { label: 'Network and carriers', resources: ['connectivity', 'carrier', 'trunk', 'node'] },
  { label: 'Integrations and audit', resources: ['webhook', 'audit'] },
];

export function PermissionEditor({ level, value, onChange, held, readOnly = false }: {
  /** The role's level: permissions that cannot be held there are not offered. */
  level: ScopeLevel;
  value: ReadonlySet<string>;
  onChange: (next: Set<string>) => void;
  /** What the person editing holds; the rest are shown but cannot be switched on. */
  held: ReadonlySet<string>;
  readOnly?: boolean;
}) {
  const [filter, setFilter] = useState('');

  const groups = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const placed = new Set(AREAS.flatMap((a) => a.resources));
    const areas = [...AREAS, {
      label: 'Other',
      resources: [...new Set(PERMISSIONS.map((p) => p.resource))].filter((r) => !placed.has(r)),
    }];
    return areas.map((a) => ({
      label: a.label,
      perms: PERMISSIONS
        .filter((p) => a.resources.includes(p.resource) && permissionAllowedAt(p.key, level))
        .filter((p) => !q || p.key.includes(q) || p.description.toLowerCase().includes(q)),
    })).filter((g) => g.perms.length > 0);
  }, [filter, level]);

  const toggle = (key: string, on: boolean) => {
    const next = new Set(value);
    if (on) next.add(key); else next.delete(key);
    onChange(next);
  };
  const toggleAll = (keys: string[], on: boolean) => {
    const next = new Set(value);
    for (const k of keys) if (on) next.add(k); else next.delete(k);
    onChange(next);
  };

  return (
    <div>
      <div className="arc-row" style={{ marginBottom: 10 }}>
        <div className="arc-search">
          <Icon name="search" size={13} />
          <input className="arc-input" value={filter} onChange={(e) => setFilter(e.target.value)}
            placeholder="Search permissions" aria-label="Find a permission" />
        </div>
        <span className="arc-subtle" style={{ marginLeft: 'auto' }}>{value.size} selected</span>
      </div>

      <div className="arc-grid arc-grid-2">
        {groups.map((g) => {
          const grantable = g.perms.filter((p) => held.has(p.key)).map((p) => p.key);
          const on = g.perms.filter((p) => value.has(p.key)).length;
          const allOn = grantable.length > 0 && grantable.every((k) => value.has(k));
          return (
            <section key={g.label} className="arc-card arc-card-body">
              <div className="arc-row" style={{ marginBottom: 8 }}>
                <h3 style={{ fontSize: 13, fontWeight: 600, margin: 0 }}>{g.label}</h3>
                <span className="arc-subtle" style={{ marginLeft: 'auto' }}>{on} of {g.perms.length}</span>
                {!readOnly && grantable.length > 0 && (
                  <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                    onClick={() => toggleAll(grantable, !allOn)}>
                    {allOn ? 'None' : 'All'}
                  </button>
                )}
              </div>
              {g.perms.map((p) => {
                const canHave = held.has(p.key);
                return (
                  <label key={p.key} className="arc-row"
                    style={{ gap: 8, alignItems: 'flex-start', padding: '3px 0', opacity: canHave || value.has(p.key) ? 1 : 0.55 }}
                    title={canHave ? undefined : 'You do not hold this permission, so you cannot give it'}>
                    <input type="checkbox" checked={value.has(p.key)}
                      disabled={readOnly || !canHave}
                      onChange={(e) => toggle(p.key, e.target.checked)} style={{ marginTop: 3 }} />
                    <span className="arc-stack" style={{ gap: 0 }}>
                      <span>{p.description}</span>
                      <span className="arc-subtle arc-mono" style={{ fontSize: 11 }}>{p.key}</span>
                    </span>
                  </label>
                );
              })}
            </section>
          );
        })}
      </div>
    </div>
  );
}
